import { createContext, useContext, useMemo } from 'react'
import type { JSX } from 'react'
import type { ReactNode, RefObject } from 'react'

import { useResizeObserver } from '../../hooks/useResizeObserver'

type PageHeaderRowContextValue = {
  isNarrow: boolean | undefined
  isMedium: boolean | undefined
}

const PageHeaderRowContext = createContext<PageHeaderRowContextValue>({
  isNarrow: undefined,
  isMedium: undefined,
})

type PageHeaderRowProviderProps = {
  children: ReactNode
  observerRef: RefObject<HTMLDivElement>
}

/**
 * Must stay in sync with `@container page-header (width < 640px)` in the
 * CSS. Drift causes the hook and helper classes to disagree.
 */
export const NARROW_PAGE_HEADER_BREAKPOINT = 640

/**
 * Must stay in sync with `@container page-header (width < 730px)` in the
 * CSS. The medium tier is where filters collapse before tabs do.
 */
export const MEDIUM_PAGE_HEADER_BREAKPOINT = 730

export function PageHeaderRowProvider({
  children,
  observerRef,
}: PageHeaderRowProviderProps): JSX.Element {
  // Container size queries measure the container's content box, so the
  // observer reads content-box (default) too — stays in sync with the
  // `@container page-header` helpers around each breakpoint.
  const [, { width }] = useResizeObserver<HTMLDivElement>({
    ref: observerRef,
  })

  // Derive the booleans inside the provider so consumers only re-render
  // when a threshold is crossed, not on every resize pixel.
  const isNarrow =
    width === undefined ? undefined : width < NARROW_PAGE_HEADER_BREAKPOINT
  const isMedium =
    width === undefined ? undefined : width < MEDIUM_PAGE_HEADER_BREAKPOINT
  const value = useMemo(() => ({ isNarrow, isMedium }), [isNarrow, isMedium])

  return (
    <PageHeaderRowContext.Provider value={value}>
      {children}
    </PageHeaderRowContext.Provider>
  )
}

/**
 * `true`/`false`/`undefined` (until first measurement). Use for structural
 * swaps (filters → sheet). For show/hide, prefer the
 * `pageHeaderStyles.hideOnNarrow` / `hideOnWide` helper classes — zero-JS
 * and always in sync with the CSS.
 */
export function useIsNarrowPageHeader(): boolean | undefined {
  return useContext(PageHeaderRowContext).isNarrow
}

/**
 * `true`/`false`/`undefined` (until first measurement). Companion to the
 * `pageHeaderStyles.hideOnMedium` CSS helper, for cases where the swap
 * needs to be structural rather than show/hide (sheet trigger, stateful
 * component, portal target). Prefer the CSS helper otherwise.
 */
export function useIsMediumPageHeader(): boolean | undefined {
  return useContext(PageHeaderRowContext).isMedium
}
